---
title: "Recover failed payments before they become *churn*."
excerpt: "A retry schedule, a clear email cadence, and a fallback path turn most involuntary churn into recovered revenue."
category: "Dunning"
author: alain
date: "2026-05-09"
readTime: "5 min read"
deck: "Most cancelled subscriptions were never cancelled. A card *expired* and nobody followed up."
---

A meaningful share of subscription churn is involuntary: the customer wanted to stay,
but a card expired, a bank declined a charge, or a transient network error dropped a
payment. None of that is a decision to leave, yet without a recovery process it counts
against you exactly like one. Dunning is the discipline of turning failed payments back
into paid ones before they harden into churn.

## Retry on a schedule, not on a whim

Issuers decline payments for very different reasons, and retrying a hard decline
immediately just burns goodwill. A good retry schedule spaces attempts out, avoids
re-trying declines that will never clear, and times retries for when balances are
likely to be funded.

- **Soft declines** (insufficient funds, temporary holds) — retry with increasing
  spacing over several days.
- **Hard declines** (closed account, stolen card) — stop retrying and ask the customer
  to update their method.
- **Expired cards** — prompt for a new method ahead of the renewal, not after it fails.

## Make the email cadence clear, not noisy

The communication around a failed payment matters as much as the retry logic. A short,
factual sequence, what failed, what you'll try next, and a one-click way to fix it,
recovers more revenue than either silence or a barrage of alarmed messages.

> The goal of a dunning email isn't to apply pressure. It's to make fixing the problem
> the easiest thing the customer does that day.

## Give it a fallback path

When retries and reminders don't land, a fallback keeps the relationship alive: a
grace period before access is cut, a downgrade to a free tier instead of a hard
cancellation, or a brief pause. Recovered revenue is cheaper than reacquired revenue
by a wide margin, and the customer who was never really trying to leave is the easiest
one to keep.
